import { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAccountOverview, ACCOUNT_OVERVIEW_SESSION_KEY } from '../context/AccountOverviewContext';
import { isExtraPage } from '../utils/extraPages';
import { CalendarDate } from "@internationalized/date";
import moment from "moment";
export const useDateRange = (options = {}) => {
    const cache = useSelector((state) => state.utility.cache);
    const location = useLocation();
    const isCacheLoaded = useRef(false);
    const isUrlLoaded = useRef(false);
    // Prevents context useEffect from overriding dates that were already correctly
    // initialised from sessionStorage (written synchronously before navigation).
    const isNavLoaded = useRef(false);
    const { selectedMonth: contextMonth, dateRange: contextRange, compareDateRange: contextCompare, isCompareOn: contextCompareOn } = useAccountOverview();
    const isExtra = isExtraPage(location.pathname);
    // ✅ Load from Context (Highest Priority for Extra Pages)
    useEffect(() => {
        // Skip on the very first run after a navigation-based mount — sessionStorage
        // already has the correct dates and context may still be stale.
        if (isNavLoaded.current) {
            isNavLoaded.current = false;
            return;
        }
        // Priority 1: Specific Date Range from Context
        if (contextRange?.start && contextRange?.end) {
            const startM = moment(contextRange.start);
            const endM = moment(contextRange.end);
            setDateRange({
                start: new CalendarDate(startM.year(), startM.month() + 1, startM.date()),
                end: new CalendarDate(endM.year(), endM.month() + 1, endM.date())
            });
            if (contextCompare?.start && contextCompare?.end) {
                const csM = moment(contextCompare.start);
                const ceM = moment(contextCompare.end);
                setCompareDateRange({
                    start: new CalendarDate(csM.year(), csM.month() + 1, csM.date()),
                    end: new CalendarDate(ceM.year(), ceM.month() + 1, ceM.date())
                });
            }
            else {
                setCompareDateRange(null);
            }
            isUrlLoaded.current = true;
            isCacheLoaded.current = true;
        }
        // Priority 2: Month from Context
        else if (contextMonth && (!isUrlLoaded.current || isExtra)) {
            const m = moment(contextMonth, 'YYYY-MM');
            if (m.isValid()) {
                let start = m.clone().startOf('month');
                let end = m.clone().endOf('month');
                // If it's the current month, end date should be yesterday
                const today = moment();
                if (m.isSame(today, 'month')) {
                    const yesterday = today.clone().subtract(1, 'day');
                    if (!yesterday.isSame(m, 'month')) {
                        start = yesterday.clone().startOf('month');
                    }
                    end = yesterday.clone();
                }
                setDateRange({
                    start: new CalendarDate(start.year(), start.month() + 1, start.date()),
                    end: new CalendarDate(end.year(), end.month() + 1, end.date())
                });
                // Set default comparison (MoM)
                const compStart = start.clone().subtract(1, 'month');
                const compEnd = end.clone().subtract(1, 'month');
                setCompareDateRange({
                    start: new CalendarDate(compStart.year(), compStart.month() + 1, compStart.date()),
                    end: new CalendarDate(compEnd.year(), compEnd.month() + 1, compEnd.date())
                });
                isUrlLoaded.current = true;
                isCacheLoaded.current = true;
            }
        }
    }, [contextRange?.start, contextRange?.end, contextCompare?.start, contextCompare?.end, contextMonth]);
    // ✅ Load from URL (Priority 2)
    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const urlMonth = queryParams.get('month'); // Expects YYYY-MM
        if (urlMonth && !isUrlLoaded.current) {
            // Skip URL loading if we are on an extra page and context is already set
            if (isExtra && (contextRange?.start || contextMonth)) {
                return;
            }
            const m = moment(urlMonth, 'YYYY-MM');
            if (m.isValid()) {
                let start = m.clone().startOf('month');
                let end = m.clone().endOf('month');
                // If it's the current month, end date should be yesterday
                const today = moment();
                if (m.isSame(today, 'month')) {
                    const yesterday = today.clone().subtract(1, 'day');
                    // Ensure yesterday is still within the same month
                    if (!yesterday.isSame(m, 'month')) {
                        start = yesterday.clone().startOf('month');
                    }
                    end = yesterday.clone();
                }
                setDateRange({
                    start: new CalendarDate(start.year(), start.month() + 1, start.date()),
                    end: new CalendarDate(end.year(), end.month() + 1, end.date())
                });
                // Set default comparison for this specific month (MoM)
                const compStart = start.clone().subtract(1, 'month');
                const compEnd = end.clone().subtract(1, 'month');
                setCompareDateRange({
                    start: new CalendarDate(compStart.year(), compStart.month() + 1, compStart.date()),
                    end: new CalendarDate(compEnd.year(), compEnd.month() + 1, compEnd.date())
                });
                isUrlLoaded.current = true;
                isCacheLoaded.current = true; // Skip cache if URL is loaded
            }
        }
    }, [location.search]);
    // ✅ Default Dates - If today is the 1st, default to Last Month. Otherwise, This Month.
    const isFirstDayOfMonth = moment().date() === 1;
    const defaultStartMoment = isFirstDayOfMonth
        ? moment().subtract(1, 'month').startOf('month')
        : moment().startOf('month');
    const defaultEndMoment = options.defaultEndToToday
        ? moment()
        : moment().subtract(1, 'day');
    const defaultStart = new CalendarDate(defaultStartMoment.year(), defaultStartMoment.month() + 1, defaultStartMoment.date());
    const defaultEnd = new CalendarDate(defaultEndMoment.year(), defaultEndMoment.month() + 1, defaultEndMoment.date());
    // ✅ Default Compare Dates (based on type)
    const getDefaultCompareDates = () => {
        const compareType = options.defaultCompareType || 'mom';
        if (compareType === 'yoy') {
            const startM = defaultStartMoment.clone().subtract(1, 'year');
            const endM = defaultEndMoment.clone().subtract(1, 'year');
            return {
                start: new CalendarDate(startM.year(), startM.month() + 1, startM.date()),
                end: new CalendarDate(endM.year(), endM.month() + 1, endM.date())
            };
        }
        // MOM
        const startM = defaultStartMoment.clone().subtract(1, 'month');
        const endM = defaultEndMoment.clone().subtract(1, 'month');
        return {
            start: new CalendarDate(startM.year(), startM.month() + 1, startM.date()),
            end: new CalendarDate(endM.year(), endM.month() + 1, endM.date())
        };
    };
    // ✅ State — lazy-init reads sessionStorage first (always written synchronously before
    // navigation) so the very first render shows the correct dates.
    const [dateRange, setDateRange] = useState(() => {
        // 0a. initialDateRange — explicit saved dates, highest priority
        if (options.initialDateRange?.start && options.initialDateRange?.end) {
            const startM = moment(options.initialDateRange.start);
            const endM = moment(options.initialDateRange.end);
            if (startM.isValid() && endM.isValid() && startM.isSameOrBefore(endM)) {
                isCacheLoaded.current = true;
                isUrlLoaded.current = true;
                isNavLoaded.current = true;
                return {
                    start: new CalendarDate(startM.year(), startM.month() + 1, startM.date()),
                    end: new CalendarDate(endM.year(), endM.month() + 1, endM.date())
                };
            }
        }
        // 0b. initialMonth option — highest priority, set by caller (e.g. navigating from AccountPerformance)
        if (options.initialMonth) {
            const m = moment(options.initialMonth, 'YYYY-MM');
            if (m.isValid()) {
                let start = m.clone().startOf('month');
                const todayM = moment();
                let end = m.clone().endOf('month');
                if (m.isSame(todayM, 'month')) {
                    const yesterday = todayM.clone().subtract(1, 'day');
                    if (!yesterday.isSame(m, 'month')) {
                        start = yesterday.clone().startOf('month');
                    }
                    end = yesterday.clone();
                }
                isCacheLoaded.current = true;
                isUrlLoaded.current = true;
                isNavLoaded.current = true;
                return {
                    start: new CalendarDate(start.year(), start.month() + 1, start.date()),
                    end: new CalendarDate(end.year(), end.month() + 1, end.date())
                };
            }
        }
        // 1. sessionStorage — written synchronously in the click handler before navigate()
        try {
            const saved = JSON.parse(sessionStorage.getItem(ACCOUNT_OVERVIEW_SESSION_KEY) || '{}');
            if (saved.dateRange?.start && saved.dateRange?.end) {
                const startM = moment(saved.dateRange.start);
                const endM = moment(saved.dateRange.end);
                if (startM.isValid() && endM.isValid() && startM.isSameOrBefore(endM)) {
                    isCacheLoaded.current = true;
                    isUrlLoaded.current = true;
                    isNavLoaded.current = true;
                    return {
                        start: new CalendarDate(startM.year(), startM.month() + 1, startM.date()),
                        end: new CalendarDate(endM.year(), endM.month() + 1, endM.date())
                    };
                }
            }
            if (saved.selectedMonth) {
                const m = moment(saved.selectedMonth, 'YYYY-MM');
                if (m.isValid()) {
                    let start = m.clone().startOf('month');
                    const today = moment();
                    let end = m.clone().endOf('month');
                    if (m.isSame(today, 'month')) {
                        const yesterday = today.clone().subtract(1, 'day');
                        if (!yesterday.isSame(m, 'month')) {
                            start = yesterday.clone().startOf('month');
                        }
                        end = yesterday.clone();
                    }
                    isCacheLoaded.current = true;
                    isUrlLoaded.current = true;
                    isNavLoaded.current = true;
                    return {
                        start: new CalendarDate(start.year(), start.month() + 1, start.date()),
                        end: new CalendarDate(end.year(), end.month() + 1, end.date())
                    };
                }
            }
        }
        catch (_) { }
        // 2. React context (fallback when sessionStorage is unavailable)
        if (contextRange?.start && contextRange?.end) {
            const startM = moment(contextRange.start);
            const endM = moment(contextRange.end);
            if (startM.isValid() && endM.isValid() && startM.isSameOrBefore(endM)) {
                return {
                    start: new CalendarDate(startM.year(), startM.month() + 1, startM.date()),
                    end: new CalendarDate(endM.year(), endM.month() + 1, endM.date())
                };
            }
        }
        return { start: defaultStart, end: defaultEnd };
    });
    const [compareDateRange, setCompareDateRange] = useState(() => {
        // 0a. initialCompareDateRange — explicit saved compare dates
        if (options.initialCompareDateRange?.start && options.initialCompareDateRange?.end) {
            const startM = moment(options.initialCompareDateRange.start);
            const endM = moment(options.initialCompareDateRange.end);
            if (startM.isValid() && endM.isValid()) {
                return {
                    start: new CalendarDate(startM.year(), startM.month() + 1, startM.date()),
                    end: new CalendarDate(endM.year(), endM.month() + 1, endM.date())
                };
            }
        }
        // 0b. initialMonth option — derive MoM comparison
        if (options.initialMonth) {
            const m = moment(options.initialMonth, 'YYYY-MM');
            if (m.isValid()) {
                const todayM = moment();
                let mainStart = m.clone().startOf('month');
                let mainEnd = m.clone().endOf('month');
                if (m.isSame(todayM, 'month')) {
                    const yesterday = todayM.clone().subtract(1, 'day');
                    if (!yesterday.isSame(m, 'month')) {
                        mainStart = yesterday.clone().startOf('month');
                    }
                    mainEnd = yesterday.clone();
                }
                const compStart = mainStart.clone().subtract(1, 'month');
                const compEnd = mainEnd.clone().subtract(1, 'month');
                return {
                    start: new CalendarDate(compStart.year(), compStart.month() + 1, compStart.date()),
                    end: new CalendarDate(compEnd.year(), compEnd.month() + 1, compEnd.date())
                };
            }
        }
        // 1. sessionStorage
        try {
            const saved = JSON.parse(sessionStorage.getItem(ACCOUNT_OVERVIEW_SESSION_KEY) || '{}');
            if (saved.compareDateRange?.start && saved.compareDateRange?.end) {
                const csM = moment(saved.compareDateRange.start);
                const ceM = moment(saved.compareDateRange.end);
                if (csM.isValid() && ceM.isValid() && csM.isSameOrBefore(ceM)) {
                    return {
                        start: new CalendarDate(csM.year(), csM.month() + 1, csM.date()),
                        end: new CalendarDate(ceM.year(), ceM.month() + 1, ceM.date())
                    };
                }
            }
        }
        catch (_) { }
        // 2. React context
        if (contextCompare?.start && contextCompare?.end) {
            const csM = moment(contextCompare.start);
            const ceM = moment(contextCompare.end);
            if (csM.isValid() && ceM.isValid() && csM.isSameOrBefore(ceM)) {
                return {
                    start: new CalendarDate(csM.year(), csM.month() + 1, csM.date()),
                    end: new CalendarDate(ceM.year(), ceM.month() + 1, ceM.date())
                };
            }
        }
        return options.defaultComparison !== false ? getDefaultCompareDates() : null;
    });
    // ✅ Load from cache
    useEffect(() => {
        if (!options.ignoreCache && cache && Object.keys(cache).length > 0 && !isCacheLoaded.current) {
            // Main dates
            if (cache.startDate && cache.endDate) {
                const startMoment = moment(cache.startDate);
                const endMoment = moment(cache.endDate);
                if (startMoment.isValid() && endMoment.isValid() && startMoment.isSameOrBefore(endMoment)) {
                    setDateRange({
                        start: new CalendarDate(startMoment.year(), startMoment.month() + 1, startMoment.date()),
                        end: new CalendarDate(endMoment.year(), endMoment.month() + 1, endMoment.date())
                    });
                }
            }
            // Compare dates
            if (cache.comparison && cache.compareStartDate && cache.compareEndDate) {
                const compareStartMoment = moment(cache.compareStartDate);
                const compareEndMoment = moment(cache.compareEndDate);
                if (compareStartMoment.isValid() && compareEndMoment.isValid() && compareStartMoment.isSameOrBefore(compareEndMoment)) {
                    setCompareDateRange({
                        start: new CalendarDate(compareStartMoment.year(), compareStartMoment.month() + 1, compareStartMoment.date()),
                        end: new CalendarDate(compareEndMoment.year(), compareEndMoment.month() + 1, compareEndMoment.date())
                    });
                }
            }
            else if (cache.comparison === false) {
                setCompareDateRange(null);
            }
            isCacheLoaded.current = true;
        }
    }, [cache]);
    // ✅ Stable string keys for dependencies
    const dateRangeKey = useMemo(() => {
        if (!dateRange?.start || !dateRange?.end)
            return '';
        const today = moment().format("YYYY-MM-DD");
        const isCurrentMonth = moment(dateRange.start.toString()).isSame(moment(), 'month');
        let end = dateRange.end.toString();
        if (!options.defaultEndToToday && isCurrentMonth && end === today) {
            end = moment().subtract(1, "day").format("YYYY-MM-DD");
        }
        return `${dateRange.start.toString()}_${end}`;
    }, [dateRange, options.defaultEndToToday]);
    const compareDateRangeKey = useMemo(() => {
        if (!compareDateRange?.start || !compareDateRange?.end)
            return '';
        return `${compareDateRange.start.toString()}_${compareDateRange.end.toString()}`;
    }, [compareDateRange]);
    // ✅ Check if comparison is enabled
    const isCompareOn = !!(compareDateRange?.start && compareDateRange?.end);
    // ✅ Handler for date picker
    const handleDateChange = (data) => {
        setDateRange(data.mainRange);
        setCompareDateRange(data.compareRange);
    };
    // ✅ Get formatted dates for API
    const getApiDates = () => ({
        startDate: dateRange?.start?.toString() || '',
        endDate: dateRange?.end?.toString() || '',
        comparison: isCompareOn,
        ...(isCompareOn && {
            compareStartDate: compareDateRange?.start?.toString(),
            compareEndDate: compareDateRange?.end?.toString()
        })
    });
    return {
        // State
        dateRange,
        compareDateRange,
        setDateRange,
        setCompareDateRange,
        // Keys for dependencies
        dateRangeKey,
        compareDateRangeKey,
        // Helpers
        isCompareOn,
        handleDateChange,
        getApiDates
    };
};
